import { RecordData } from '@/@admin/types/entities';
import { SEMVER_REGEX } from '@/@admin/types/constants';

/**
 * Gets the value of a specific property from a RecordData object
 * @param record - The RecordData object from which to retrieve the property value
 * @param propertyName - The name of the property to retrieve
 * @returns The value of the specified property, or undefined if it does not exist
 */
export const getPropertyValue = (record: Partial<RecordData>, propertyName: string): unknown => {
    if (record && typeof record === 'object' && propertyName in record) {
        return (record as any)[propertyName];
    }
    return undefined;
};

/**
 * Normalizes Salesforce data to a consistent format for dropdowns
 * @param data - Array of Salesforce objects
 * @returns Normalized array with id and name properties
 */
export function normalizeSalesforceData(data: any[]): { id: string; name: string }[] {
    if (!Array.isArray(data) || !data.length) {
        return [];
    }

    return data.map((item) => {
        // Handle Contact objects (use Email for both id and name)
        if (item.Email) {
            return {
                id: item.Email,
                name: item.Email,
            };
        }

        // Handle Account/Opportunity objects (use Id and Name)
        if (item.Id && item.Name) {
            return {
                id: item.Id,
                name: item.Name,
            };
        }

        // Fallback for unexpected data structure
        throw new Error(`Unexpected Salesforce data structure: ${JSON.stringify(item)}`);
    });
}

/**
 * Gets the icon for a feedback status
 * @param isPositive - Whether the feedback is positive
 * @returns The icon for the feedback status
 */
export const getFeedbackIcon = (isPositive: boolean) => {
    return isPositive ? 'lucide:thumbs-up' : 'lucide:thumbs-down';
};

/**
 * Validates that a version string follows semantic versioning (X.Y.Z) format
 * @param version - The version string to validate
 * @returns True if the version matches the X.Y.Z format, false otherwise
 */
export const isValidVersion = (version: string): boolean => SEMVER_REGEX.test(version);

/**
 * Formats a date string into a human-readable format (e.g. "Jan 1, 2024, 12:00 PM")
 * @param date - The ISO date string to format, or null
 * @returns A formatted date string, or '-' if the date is null or empty
 */
export const formatDate = (date: string | null): string => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};
